/**
 * Most pada i pilota Bluetooth na klawiaturę.
 *
 * Pad nie wysyła zdarzeń — trzeba go **odpytywać**, i to jest cała trudność:
 * `navigator.getGamepads()` zwraca migawkę stanu, więc bez pamięci poprzedniej
 * klatki nie da się odróżnić „nacisnął" od „trzyma". Bez tego rozróżnienia jedno
 * naciśnięcie w dół przeskakiwałoby przez cały ekran w ćwierć sekundy.
 *
 * Zdarzenia wysyłamy jako **syntetyczne `keydown`**, zamiast wołać nawigację
 * wprost. Dzięki temu pad, pilot telewizora i klawiatura mają jedną drogę do tego
 * samego kodu: nawigacja strzałkami, skróty odtwarzacza i `Enter` na przycisku
 * działają bez wiedzy o tym, co je wywołało.
 */

/** Standardowy układ pada: cztery kierunki krzyżaka. */
const DPAD: Record<number, string> = {
  12: 'ArrowUp',
  13: 'ArrowDown',
  14: 'ArrowLeft',
  15: 'ArrowRight',
};

/** Przyciski akcji. `A` zatwierdza, `B` cofa — jak na każdej konsoli. */
const ACTIONS: Record<number, string> = {
  0: 'Enter',
  1: 'Escape',
};

/** Wychylenie drążka, od którego traktujemy je jak kierunek. */
const STICK_THRESHOLD = 0.6;

/** Ile czekać przed pierwszym powtórzeniem przy przytrzymaniu. */
const REPEAT_DELAY_MS = 400;

/** Odstęp kolejnych powtórzeń. Trzy na sekundę to tempo, przy którym widać, gdzie się jest. */
const REPEAT_INTERVAL_MS = 300;

export interface GamepadFrame {
  /** Klawisze wciśnięte w tej klatce, po odfiltrowaniu powtórzeń. */
  keys: string[];
}

interface HeldKey {
  since: number;
  lastFired: number;
}

/**
 * Stan mostu. Wydzielony z pętli, bo tylko on decyduje o powtórzeniach — a to
 * jedyna część, którą da się sprawdzić bez podłączonego pada.
 */
export class GamepadBridge {
  private held = new Map<string, HeldKey>();

  /**
   * Przyjmuje surowy stan i zwraca klawisze do wysłania.
   *
   * Pierwsze naciśnięcie wychodzi natychmiast; przytrzymanie powtarza się dopiero
   * po `REPEAT_DELAY_MS`, a potem co `REPEAT_INTERVAL_MS`. Zwolnienie kasuje pamięć,
   * więc kolejne naciśnięcie znów jest natychmiastowe.
   */
  step(pressed: Set<string>, now: number): string[] {
    const out: string[] = [];

    for (const key of pressed) {
      const state = this.held.get(key);
      if (state === undefined) {
        this.held.set(key, { since: now, lastFired: now });
        out.push(key);
        continue;
      }
      const waited = now - state.since;
      const sinceLast = now - state.lastFired;
      if (waited >= REPEAT_DELAY_MS && sinceLast >= REPEAT_INTERVAL_MS) {
        state.lastFired = now;
        out.push(key);
      }
    }

    // Zwolnione klawisze wypadają z pamięci; bez tego przytrzymanie raz na zawsze
    // ustawiałoby tempo powtórzeń dla następnych naciśnięć.
    for (const key of [...this.held.keys()]) {
      if (!pressed.has(key)) this.held.delete(key);
    }

    return out;
  }
}

/**
 * Odczytuje stan padów i zwraca zbiór „wciśniętych" klawiszy.
 *
 * Drążek i krzyżak dają ten sam wynik: na pilotach z drążkiem krzyżaka bywa brak,
 * a widz i tak oczekuje, że wychylenie w bok przesuwa zaznaczenie.
 */
export function readPads(pads: ReadonlyArray<Gamepad | null>): Set<string> {
  const pressed = new Set<string>();

  for (const pad of pads) {
    if (pad === null || !pad.connected) continue;

    for (const [index, key] of Object.entries({ ...DPAD, ...ACTIONS })) {
      if (pad.buttons[Number(index)]?.pressed === true) pressed.add(key);
    }

    const [x = 0, y = 0] = pad.axes;
    if (x <= -STICK_THRESHOLD) pressed.add('ArrowLeft');
    if (x >= STICK_THRESHOLD) pressed.add('ArrowRight');
    if (y <= -STICK_THRESHOLD) pressed.add('ArrowUp');
    if (y >= STICK_THRESHOLD) pressed.add('ArrowDown');
  }

  return pressed;
}
